import { LoginContext } from '@local/login/components/loginHandler/LoginContext';
import { PRIVACY_POLICY_LINK, RIGHTS_RESERVED, TERMS_OF_USE_LINK } from '@local/login/strings';
import { useTrace } from '@local/web-design-system/utils/trace';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/GridLegacy';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import classnames from 'classnames';
import type { PropsWithChildren } from 'react';
import { useContext } from 'react';

import { LoginForm } from 'src/components/login/form';
import { LoginSideBar } from 'src/components/login/side-bar';
import { Logo } from 'src/components/logo';
import { ApiSelector } from 'src/components/top-bar/api-selector';
import { getIsProduction } from 'src/utils/get-is-production';

import { useHiddenApiSelectorStyles, useStyles } from './LandingPage.styles';

type LandingBackgroundProps = {
    trainingLink?: string;
};

const HiddenAPISelector = () => {
    const { classes } = useHiddenApiSelectorStyles();

    return (
        <div className={classes.hiddenApiSelector}>
            <ApiSelector />
        </div>
    );
};

const LandingBackground = ({
    children,
    trainingLink,
}: PropsWithChildren<LandingBackgroundProps>) => {
    const { imageList } = useContext(LoginContext);
    const { classes } = useStyles({ imageList });
    const applyTrace = useTrace('landingPage');

    const isProduction = getIsProduction();

    return (
        <Grid container className={classes.root} automation-id="background-root">
            {!isProduction && <HiddenAPISelector />}
            <Grid item md={8} lg={9} sm={12}>
                <Grid container alignItems="center">
                    <Grid item xs={12} sm={6} automation-id="background-logo-title">
                        <Grid
                            container
                            className={classnames([classes.column, classes.columnLeft])}
                        >
                            <Logo />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Grid
                            container
                            className={classnames([
                                classes.formContainer,
                                classes.column,
                                classes.columnRight,
                            ])}
                        >
                            {children}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item justifyContent="left" alignItems="flex-start" className={classes.copy}>
                    <Typography data-dd-privacy="allow">{RIGHTS_RESERVED}</Typography>
                </Grid>
                <Grid container direction="row" className={classes.footer}>
                    <Typography variant="body2" paddingRight={1} data-dd-privacy="allow">
                        <span key={TERMS_OF_USE_LINK.linkedText}>
                            <Link
                                href={TERMS_OF_USE_LINK.href}
                                target="_blank"
                                color="inherit"
                                rel="noopener"
                                underline="always"
                                {...applyTrace('terms-of-use-link')}
                            >
                                {TERMS_OF_USE_LINK.linkedText}
                            </Link>
                        </span>
                    </Typography>
                    <Divider className={classes.divider} orientation="vertical" />
                    <Typography variant="body2" paddingLeft={1} data-dd-privacy="allow">
                        <span key={PRIVACY_POLICY_LINK.linkedText}>
                            <Link
                                href={PRIVACY_POLICY_LINK.href}
                                target="_blank"
                                color="inherit"
                                rel="noopener"
                                underline="always"
                                {...applyTrace('privacy-policy-link')}
                            >
                                {PRIVACY_POLICY_LINK.linkedText}
                            </Link>
                        </span>
                    </Typography>
                </Grid>
            </Grid>
            <Grid item md={4} lg={3} sx={{ display: { xs: 'none', md: 'block' } }}>
                <LoginSideBar trainingLink={trainingLink} />
            </Grid>
        </Grid>
    );
};

export const LandingPage = () => {
    return (
        <LandingBackground>
            <LoginForm />
        </LandingBackground>
    );
};
